import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import RequestV2 from "api/RequestV2";
import Config from "utils/config";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";

import {
    PageTitle,
    Card,
    Table,
    PageLoader,
    ListPageFilterBar,
    ErrorMessage,
    EditIcon,
} from "../../design-system";

import { CsvDown, PdfDown } from "../../integrations/export";
import { theme } from "../../design-system/theme";
import TidFormV2 from "./TidFormV2";
import { Button } from "../../design-system";

const PageWrapper = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: ${theme.spacing.sm} 0 0;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const ActionsRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const FILTER_INITIAL = [
    { name: "tid", type: "text", value: "", placeholder: "TID" },
    { name: "orgCode", type: "text", value: "", placeholder: "Org Code" },
    {
        name: "isActive",
        type: "select",
        value: "",
        placeholder: "Status",
        options: [
            { value: "true", label: "Active" },
            { value: "false", label: "Inactive" },
        ],
        placeholderOnly: true,
    },
];

export default function TidWhitelistingV2() {
    const [list, setList] = useState([]);
    const [controls, setControls] = useState(FILTER_INITIAL);
    const controlsRef = useRef(FILTER_INITIAL);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [total, setTotal] = useState(0);

    const [showForm, setShowForm] = useState(false);
    const [editData, setEditData] = useState(null);

    const [deleteId, setDeleteId] = useState(null);
    const [showDelete, setShowDelete] = useState(false);

    useEffect(() => {
        controlsRef.current = controls;
    }, [controls]);

    const buildPayload = () => {
        const getVal = (name) => {
            const field = controlsRef.current.find((c) => c.name === name);

            if (name === "isActive") {
                if (field?.value === "true") return true;
                if (field?.value === "false") return false;
                return null;
            }

            return field?.value === "" ? null : field?.value;
        };

        return {
            tid: getVal("tid"),
            orgCode: getVal("orgCode"),
            isActive: getVal("isActive"),
            sortBy: "createdAt",
            sortDirection: "DESC",
        };
    };

    const fetchList = useCallback(() => {
        setLoading(true);
        setError("");

        const api = new RequestV2(
            "",
            (res) => {
                setLoading(false);

                const data = res?.data || res;

                const content = data?.data?.content || data?.content || [];
                setList(Array.isArray(content) ? content : []);

                const totalElements =
                    data?.data?.totalElements ||
                    data?.totalElements ||
                    content.length;

                setTotal(totalElements);
            },
            (err) => {
                setLoading(false);
                setError(err?.message || "Failed to fetch TID list");
            }
        );

        const payload = buildPayload();

        if (!isFilterApplied()) {
            return api.get(
                `${Config.apis.admin.TID_WHITELISTING}active/?page=${page - 1}&size=${size}&sortBy=createdAt&sortDirection=DESC`
            );
        }

        return api.post(
            `${Config.apis.admin.TID_WHITELISTING}search`,
            {
                ...payload,
                page: page - 1,
                isDeleted: false,
            }
        );
    }, [page, size]);

    useEffect(() => {
        fetchList();
    }, [page, size]);

    const handleFilterChange = (name, value) => {
        setControls((prev) =>
            prev.map((c) =>
                c.name === name ? { ...c, value } : c
            )
        );
    };

    const onSearch = () => {
        setPage(1);
        setTimeout(fetchList, 0);
    };

    const onReset = () => {
        setControls(FILTER_INITIAL.map((f) => ({ ...f, value: "" })));
        setPage(1);
        setTimeout(fetchList, 0);
    };

    const csvHeaders = [
        { label: "TID", key: "tid" },
        { label: "Org Code", key: "orgCode" },
        { label: "Active", key: "active" },
    ];

    const pdfHeader = [
        {
            tid: "TID",
            orgCode: "Org Code",
            active: "Active",
        },
    ];

    const getPdfBody = (rows) =>
        rows.map((r) => ({
            tid: r?.tid || "",
            orgCode: r?.orgCode || "",
            active: r?.active ? "Active" : "Inactive",
        }));

    if (loading && list.length === 0) return <PageLoader />;

    const isFilterApplied = () => {
        return controlsRef.current.some(
            (c) => c.value !== "" && c.value !== null && c.value !== undefined
        );
    };

    const handleCreate = () => {
        setEditData(null);
        setShowForm(true);
    };

    const handleEdit = (row) => {
        setEditData(row);
        setShowForm(true);
    };

    const handleClose = () => {
        setShowForm(false);
        setEditData(null);
    };

    const handleDeleteClick = (row) => {
        setDeleteId(row?.id);
        setShowDelete(true);
    };

    const handleDeleteClose = () => {
        setDeleteId(null);
        setShowDelete(false);
    };

    const handleDeleteConfirm = () => {
        if (!deleteId) return;

        const api = new RequestV2(
            "",
            () => {
                handleDeleteClose();
                fetchList();
            },
            (err) => {
                console.error(err);
            }
        );

        api.delete(`${Config.apis.admin.TID_WHITELISTING}${deleteId}`);
    };

    const COLUMNS = [
        { key: "tid", label: "TID", render: (_, row) => row?.tid || "—" },
        { key: "orgCode", label: "Org Code", render: (_, row) => row?.orgCode || "—" },
        {
            key: "active",
            label: "Status",
            render: (_, row) => (row?.active ? "Active" : "Inactive"),
        },
        {
            key: "createdAt",
            label: "Created At",
            render: (_, row) => row?.createdAt || "—",
        },
        {
            key: "updatedAt",
            label: "Updated At",
            render: (_, row) => row?.updatedAt || "—",
        },
        {
            key: "action",
            label: "Action",
            render: (_, row) => (
                <div style={{ display: "flex", gap: 8 }}>
                    <IconButton
                        size="small"
                        onClick={() => handleEdit(row)}
                        sx={{ color: theme.colors?.primary?.[500] ?? "#1976d2" }}
                    >
                        <EditIcon />
                    </IconButton>

                    <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(row)}
                        sx={{ color: "#d32f2f" }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </div>
            )
        }
    ];

    return (
        <PageWrapper>
            {error && <ErrorMessage error={error} />}

            <TopBar>
                <PageTitle>TID Whitelisting</PageTitle>

                <ActionsRow>
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={handleCreate}
                        style={{ alignSelf: "flex-start", width: "fit-content", maxWidth: "100%" }}
                    >
                        Create TID
                    </Button>

                    <CsvDown
                        headers={csvHeaders}
                        exportRows={list}
                        title="CSV"
                    />

                    <PdfDown
                        tableHeader={pdfHeader}
                        getTableBody={getPdfBody}
                        exportRows={list}
                        heading="TID List"
                        fileName="tid_list"
                    />
                </ActionsRow>
            </TopBar>

            <Card>
                <ListPageFilterBar
                    filters={controls}
                    onFilterChange={handleFilterChange}
                    onSearch={onSearch}
                    onReset={onReset}
                />

                <Table
                    data={list}
                    columns={COLUMNS}
                    rowKey={(row, i) => row?.tid || i}
                    loading={loading}
                    emptyMessage="No TIDs found"
                    pagination={{
                        currentPage: page,
                        pageSize: size,
                        total,
                        onPageChange: (p) => setPage(p),
                        onPageSizeChange: (s) => {
                            setSize(s);
                            setPage(1);
                        },
                    }}
                />
            </Card>
            <Dialog
                open={showForm}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    {editData ? "Edit TID" : "Create TID"}

                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <TidFormV2
                        editData={editData}
                        onSuccess={() => {
                            fetchList();

                            setTimeout(() => {
                                handleClose();
                            }, 900);
                        }}
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={showDelete}
                onClose={handleDeleteClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete TID</DialogTitle>

                <DialogContent>
                    <div style={{ marginBottom: 16 }}>
                        Are you sure you want to delete this TID?
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                        <Button variant="outline" onClick={handleDeleteClose}>
                            Cancel
                        </Button>

                        <Button variant="primary" onClick={handleDeleteConfirm}>
                            Delete
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </PageWrapper>
    );
}
